/**
 * 📊 CRUD PERFORMANCE PROFILER
 * =============================
 * 
 * Herramienta para medir y analizar el rendimiento de operaciones CRUD:
 * - Creación (New) de vigilantes, clientes, tiendas
 * - Actualización (Update) de registros existentes
 * - Carga de datos (fetch) para formularios
 * - Búsqueda y lazy loading
 * - Subida de archivos
 * 
 * @author Sistema de Profiling
 * @version 1.0.0
 */

class CRUDPerformanceProfiler {
    constructor() {
        this.sessions = new Map();
        this.currentSession = null;
        this.isEnabled = true; // Cambiar a false para desactivar profiling
    }

    /**
     * Inicia una nueva sesión de profiling
     * @param {string} operation - Tipo de operación (Create, Update, etc.)
     * @param {Object} metadata - Metadata adicional
     */
    startSession(operation, metadata = {}) {
        if (!this.isEnabled) return;

        const sessionId = `${operation}_${Date.now()}`;
        
        this.currentSession = {
            sessionId,
            operation,
            startTime: performance.now(),
            measurements: new Map(),
            metadata: {
                ...metadata,
                timestamp: new Date().toISOString(),
                userAgent: navigator.userAgent,
            }
        };

        this.sessions.set(sessionId, this.currentSession);
        
        console.log(`📊 [CRUD PROFILING] Iniciando sesión: ${sessionId}`);
        console.log(`🔧 Operación: ${operation}`);
        if (Object.keys(metadata).length > 0) {
            console.log(`📝 Metadata:`, metadata);
        }

        return sessionId;
    }

    /**
     * Inicia una medición dentro de la sesión actual
     * @param {string} measurementName - Nombre de la medición
     * @param {Object} details - Detalles adicionales
     */
    startMeasurement(measurementName, details = {}) {
        if (!this.isEnabled || !this.currentSession) {
            if (!this.currentSession && this.isEnabled) {
                console.warn(`⚠️ [CRUD PROFILING] No hay sesión activa para medir: ${measurementName}`);
            }
            return;
        }

        const measurement = {
            name: measurementName,
            startTime: performance.now(),
            details,
            subMeasurements: []
        };

        this.currentSession.measurements.set(measurementName, measurement);
        
        console.log(`⏱️ [CRUD PROFILING] ⏯️ Iniciando: ${measurementName}`, details);

        return measurementName;
    }

    /**
     * Finaliza una medición
     * @param {string} measurementName - Nombre de la medición a finalizar
     * @param {Object} result - Resultado de la medición
     */
    endMeasurement(measurementName, result = {}) {
        if (!this.isEnabled || !this.currentSession) {
            if (!this.currentSession && this.isEnabled) {
                console.warn(`⚠️ [CRUD PROFILING] No hay sesión activa para finalizar: ${measurementName}`);
            }
            return;
        }

        const measurement = this.currentSession.measurements.get(measurementName);
        
        if (!measurement) {
            console.warn(`⚠️ [CRUD PROFILING] Medición no encontrada: ${measurementName}`);
            return;
        }

        measurement.endTime = performance.now();
        measurement.duration = measurement.endTime - measurement.startTime;
        measurement.result = result;

        console.log(`⏱️ [CRUD PROFILING] ⏹️ Finalizado: ${measurementName}`);
        console.log(`⏰ Duración: ${measurement.duration.toFixed(2)}ms`);
        
        if (Object.keys(result).length > 0) {
            console.log(`📊 Resultado:`, result);
        }

        return measurement.duration;
    }

    /**
     * Finaliza la sesión de profiling y genera el reporte
     * @param {Object} summary - Resumen adicional
     */
    endSession(summary = {}) {
        if (!this.isEnabled || !this.currentSession) {
            if (!this.currentSession && this.isEnabled) {
                console.warn(`⚠️ [CRUD PROFILING] No hay sesión activa para finalizar`);
            }
            return;
        }

        this.currentSession.endTime = performance.now();
        this.currentSession.totalDuration = this.currentSession.endTime - this.currentSession.startTime;
        this.currentSession.summary = summary;

        // Generar reporte
        this.generateReport(this.currentSession);

        const sessionId = this.currentSession.sessionId;
        this.currentSession = null;

        return sessionId;
    }

    /**
     * Genera un reporte detallado de la sesión
     * @param {Object} session - Sesión a reportar
     */
    generateReport(session) {
        console.log(`📊 ================================================`);
        console.log(`📊 [CRUD PROFILING] RESUMEN DE SESIÓN: ${session.sessionId}`);
        console.log(`📊 ================================================`);
        console.log(`🔧 Operación: ${session.operation}`);
        console.log(`⏰ Tiempo total: ${session.totalDuration.toFixed(2)}ms`);
        console.log(`📅 Timestamp: ${session.metadata.timestamp}`);
        
        // Metadata específica
        if (session.metadata.entity) {
            console.log(`📦 Entidad: ${session.metadata.entity}`);
        }
        if (session.metadata.recordId) {
            console.log(`🔑 ID: ${session.metadata.recordId}`);
        }
        if (session.metadata.serviceId) {
            console.log(`🏢 Servicio: ${session.metadata.serviceId}`);
        }
        
        console.log(`📋 Desglose por función:`);
        console.log(`------------------------------------------------`);

        // Ordenar mediciones por tiempo de inicio
        const sortedMeasurements = Array.from(session.measurements.values())
            .filter(m => m.endTime) // Solo las completadas
            .sort((a, b) => a.startTime - b.startTime);

        sortedMeasurements.forEach((measurement, index) => {
            const percentage = ((measurement.duration / session.totalDuration) * 100).toFixed(1);
            const icon = measurement.duration > 5000 ? '🔴' : measurement.duration > 2000 ? '🟡' : '✅';
            
            console.log(`${index + 1}. ${icon} ${measurement.name} ${measurement.duration.toFixed(2)}ms (${percentage}%)`);
            
            // Mostrar detalles si existen
            if (Object.keys(measurement.details).length > 0) {
                console.log(`   📝 Detalles:`, measurement.details);
            }
            
            // Mostrar resultados si existen
            if (measurement.result && Object.keys(measurement.result).length > 0) {
                console.log(`   📊 Resultado:`, measurement.result);
            }
        });

        console.log(`------------------------------------------------`);
        console.log(`📊 Total de mediciones: ${sortedMeasurements.length}`);
        
        // Resumen adicional
        if (session.summary && Object.keys(session.summary).length > 0) {
            console.log(`📋 Resumen adicional:`, session.summary);
        }
        
        // Análisis de rendimiento
        this.analyzePerformance(session, sortedMeasurements);
        
        console.log(`📊 ================================================`);
    }

    /**
     * Analiza el rendimiento y genera recomendaciones
     * @param {Object} session - Sesión a analizar
     * @param {Array} measurements - Mediciones ordenadas
     */
    analyzePerformance(session, measurements) {
        console.log(`\n🔍 ANÁLISIS DE RENDIMIENTO:`);
        console.log(`------------------------------------------------`);

        const totalDuration = session.totalDuration;
        const isCreate = session.operation.includes('Create');
        const isUpdate = session.operation.includes('Update');
        
        // Clasificar rendimiento según el tipo de operación
        let performanceLevel = '';
        let recommendations = [];

        // Umbrales diferentes para Create vs Update
        const threshold = {
            excellent: isCreate ? 2000 : 1500,
            good: isCreate ? 5000 : 3000,
            acceptable: isCreate ? 10000 : 7000
        };

        if (totalDuration < threshold.excellent) {
            performanceLevel = '🚀 EXCELENTE';
            console.log(`${performanceLevel} - Tiempo de operación < ${threshold.excellent/1000}s`);
        } else if (totalDuration < threshold.good) {
            performanceLevel = '✅ BUENO';
            console.log(`${performanceLevel} - Tiempo de operación < ${threshold.good/1000}s`);
        } else if (totalDuration < threshold.acceptable) {
            performanceLevel = '⚠️ ACEPTABLE';
            console.log(`${performanceLevel} - Tiempo de operación < ${threshold.acceptable/1000}s`);
            recommendations.push('Considerar optimizaciones para mejorar UX');
        } else {
            performanceLevel = '🔴 MEJORABLE';
            console.log(`${performanceLevel} - Tiempo de operación > ${threshold.acceptable/1000}s`);
            recommendations.push('Requiere optimización urgente');
        }

        // Identificar cuellos de botella
        const bottlenecks = measurements.filter(m => m.duration > 2000);
        
        if (bottlenecks.length > 0) {
            console.log(`\n🔴 Cuellos de botella identificados (> 2s):`);
            bottlenecks.forEach(bottleneck => {
                console.log(`   - ${bottleneck.name}: ${bottleneck.duration.toFixed(2)}ms`);
                
                // Recomendaciones específicas
                if (bottleneck.name.includes('LoadInitialData') || bottleneck.name.includes('FetchData')) {
                    recommendations.push(`Optimizar ${bottleneck.name}: Implementar caché o cargar solo datos necesarios`);
                }
                if (bottleneck.name.includes('UploadImage') || bottleneck.name.includes('UploadFile')) {
                    recommendations.push(`Optimizar ${bottleneck.name}: Comprimir imágenes antes de subir`);
                }
                if (bottleneck.name.includes('Search')) {
                    recommendations.push(`Optimizar ${bottleneck.name}: Implementar índices en Firestore`);
                }
                if (bottleneck.name.includes('LazyLoad')) {
                    recommendations.push(`Optimizar ${bottleneck.name}: Reducir tamaño de página`);
                }
                if (bottleneck.name.includes('Validate')) {
                    recommendations.push(`Optimizar ${bottleneck.name}: Mover validaciones complejas al servidor`);
                }
            });
        }

        // Recomendaciones generales
        if (recommendations.length > 0) {
            console.log(`\n💡 Recomendaciones:`);
            [...new Set(recommendations)].forEach((rec, index) => {
                console.log(`   ${index + 1}. ${rec}`);
            });
        }

        // Información sobre éxito de la operación
        if (session.summary.success !== undefined) {
            console.log(`\n📊 Estado de la operación:`);
            console.log(`   - Éxito: ${session.summary.success ? '✅ SI' : '❌ NO'}`);
            if (session.summary.error) {
                console.log(`   - Error: ${session.summary.error}`);
            }
        }

        // Advertencias específicas
        if (totalDuration > 10000 && !bottlenecks.some(b => b.name.includes('Upload'))) {
            console.log(`\n⚠️ ADVERTENCIA:`);
            console.log(`   Tiempo muy alto sin subida de archivos.`);
            console.log(`   Posibles causas:`);
            console.log(`   - Consultas Firestore no optimizadas`);
            console.log(`   - Lazy loading con demasiados items`);
            console.log(`   - Procesamiento client-side pesado`);
        }
    }

    /**
     * Obtiene un reporte de todas las sesiones
     */
    getAllSessions() {
        return Array.from(this.sessions.values());
    }

    /**
     * Limpia todas las sesiones almacenadas
     */
    clearSessions() {
        this.sessions.clear();
        this.currentSession = null;
        console.log(`🗑️ [CRUD PROFILING] Sesiones limpiadas`);
    }

    /**
     * Habilita o deshabilita el profiling
     * @param {boolean} enabled - Estado del profiling
     */
    setEnabled(enabled) {
        this.isEnabled = enabled;
        console.log(`📊 [CRUD PROFILING] Profiling ${enabled ? 'habilitado' : 'deshabilitado'}`);
    }
}

// Crear instancia única (singleton)
const crudProfiler = new CRUDPerformanceProfiler();

// Exportar como default y named export
export default crudProfiler;
export { CRUDPerformanceProfiler, crudProfiler };

